import { DateTime } from 'luxon';

/**
 * Utility functions for timezone detection and handling
 */

/**
 * Get the user's timezone using JavaScript's Intl.DateTimeFormat
 * This is the most reliable method for getting the user's actual timezone
 * @returns The user's timezone (e.g., "America/New_York", "Europe/London", "Asia/Kolkata")
 */
export const getUserTimezone = (): string => {
  try {
    // Use Intl.DateTimeFormat to get the timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timezone || 'UTC';
  } catch (error) {
    console.warn('Failed to detect timezone, falling back to UTC:', error);
    return 'UTC';
  }
};


/**
 * Convert timezone offset to timezone string format
 * @param offsetMinutes Timezone offset in minutes
 * @returns Timezone string (e.g., "UTC-5", "UTC+5:30")
 */
export const offsetToTimezoneString = (offsetMinutes: number): string => {
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const minutes = Math.abs(offsetMinutes) % 60;
  const sign = offsetMinutes <= 0 ? '+' : '-';
  
  if (minutes === 0) {
    return `UTC${sign}${hours}`;
  } else {
    return `UTC${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
  }
};

/**
 * Pretty-label overrides for well-known zones. Entries not listed here fall
 * back to the IANA identifier (with underscores replaced by spaces).
 */
const TIMEZONE_DISPLAY_NAMES: { [key: string]: string } = {
  'UTC': 'Universal Time (UTC)',
  // Americas
  'America/New_York': 'Eastern Time (ET)',
  'America/Chicago': 'Central Time (CT)',
  'America/Denver': 'Mountain Time (MT)',
  'America/Phoenix': 'Mountain Standard Time (MST)',
  'America/Los_Angeles': 'Pacific Time (PT)',
  'America/Anchorage': 'Alaska Time (AKT)',
  'America/Halifax': 'Atlantic Time (AT)',
  'America/St_Johns': 'Newfoundland Time (NT)',
  'America/Sao_Paulo': 'Brasília Time (BRT)',
  'America/Mexico_City': 'Central Time – Mexico (CT)',
  'America/Toronto': 'Eastern Time – Toronto (ET)',
  'America/Argentina/Buenos_Aires': 'Argentina Time (ART)',
  // Europe / Africa
  'Europe/London': 'Greenwich Mean Time (GMT)',
  'Europe/Dublin': 'Irish Standard Time (IST)',
  'Europe/Lisbon': 'Western European Time (WET)',
  'Europe/Paris': 'Central European Time (CET)',
  'Europe/Berlin': 'Central European Time (CET)',
  'Europe/Madrid': 'Central European Time (CET)',
  'Europe/Rome': 'Central European Time (CET)',
  'Europe/Amsterdam': 'Central European Time (CET)',
  'Europe/Brussels': 'Central European Time (CET)',
  'Europe/Zurich': 'Central European Time (CET)',
  'Europe/Vienna': 'Central European Time (CET)',
  'Europe/Stockholm': 'Central European Time (CET)',
  'Europe/Warsaw': 'Central European Time (CET)',
  'Europe/Athens': 'Eastern European Time (EET)',
  'Europe/Istanbul': 'Türkiye Time (TRT)',
  'Europe/Moscow': 'Moscow Time (MSK)',
  'Africa/Cairo': 'Eastern European Time (EET)',
  'Africa/Johannesburg': 'South Africa Standard Time (SAST)',
  'Africa/Lagos': 'West Africa Time (WAT)',
  'Africa/Nairobi': 'East Africa Time (EAT)',
  // Middle East / Asia
  'Asia/Dubai': 'Gulf Standard Time (GST)',
  'Asia/Riyadh': 'Arabia Standard Time (AST)',
  'Asia/Tehran': 'Iran Standard Time (IRST)',
  'Asia/Karachi': 'Pakistan Standard Time (PKT)',
  'Asia/Calcutta': 'Indian Standard Time (IST)',
  'Asia/Kolkata': 'Indian Standard Time (IST)',
  'Asia/Colombo': 'Sri Lanka Standard Time (SLST)',
  'Asia/Kathmandu': 'Nepal Time (NPT)',
  'Asia/Dhaka': 'Bangladesh Standard Time (BST)',
  'Asia/Yangon': 'Myanmar Time (MMT)',
  'Asia/Bangkok': 'Indochina Time (ICT)',
  'Asia/Jakarta': 'Western Indonesia Time (WIB)',
  'Asia/Kuala_Lumpur': 'Malaysia Time (MYT)',
  'Asia/Manila': 'Philippine Time (PHT)',
  'Asia/Singapore': 'Singapore Time (SGT)',
  'Asia/Hong_Kong': 'Hong Kong Time (HKT)',
  'Asia/Taipei': 'Taipei Standard Time (TST)',
  'Asia/Shanghai': 'China Standard Time (CST)',
  'Asia/Seoul': 'Korea Standard Time (KST)',
  'Asia/Tokyo': 'Japan Standard Time (JST)',
  // Australia / Pacific
  'Australia/Perth': 'Australian Western Time (AWT)',
  'Australia/Adelaide': 'Australian Central Time (ACT)',
  'Australia/Brisbane': 'Australian Eastern Standard Time (AEST)',
  'Australia/Melbourne': 'Australian Eastern Time (AET)',
  'Australia/Sydney': 'Australian Eastern Time (AET)',
  'Pacific/Auckland': 'New Zealand Time (NZT)',
  'Pacific/Fiji': 'Fiji Time (FJT)',
  'Pacific/Honolulu': 'Hawaii-Aleutian Standard Time (HST)',
};

/**
 * Get a user-friendly display name for a timezone. Falls back to the IANA
 * identifier (with underscores replaced by spaces) if no pretty label exists.
 */
export const getTimezoneDisplayName = (timezone: string): string => {
  if (TIMEZONE_DISPLAY_NAMES[timezone]) return TIMEZONE_DISPLAY_NAMES[timezone];
  return timezone.replace(/_/g, ' ');
};

/**
 * Get the short abbreviation for a timezone (e.g. "IST", "EST", "JST").
 * Extracts from the parenthesized abbreviation in TIMEZONE_DISPLAY_NAMES.
 * Falls back to Intl timeZoneName: 'short' or the IANA id.
 */
export const getTimezoneAbbreviation = (timezone: string): string => {
  const display = TIMEZONE_DISPLAY_NAMES[timezone];
  if (display) {
    const match = display.match(/\(([^)]+)\)$/);
    if (match) return match[1];
  }
  // Fallback: use Intl to get short name
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' }).formatToParts(new Date());
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    if (tzPart) return tzPart.value;
  } catch { /* ignore */ }
  return timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;
};

/**
 * Curated fallback list for environments without `Intl.supportedValuesOf`
 * (older Android WebViews / iOS Safari < 15.4).
 */
const FALLBACK_TIMEZONES: string[] = [
  'UTC',
  'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi',
  'America/Anchorage', 'America/Argentina/Buenos_Aires', 'America/Chicago',
  'America/Denver', 'America/Halifax', 'America/Los_Angeles',
  'America/Mexico_City', 'America/New_York', 'America/Phoenix',
  'America/Sao_Paulo', 'America/St_Johns', 'America/Toronto',
  'Asia/Bangkok', 'Asia/Calcutta', 'Asia/Colombo', 'Asia/Dhaka',
  'Asia/Dubai', 'Asia/Hong_Kong', 'Asia/Jakarta', 'Asia/Karachi',
  'Asia/Kathmandu', 'Asia/Kolkata', 'Asia/Kuala_Lumpur', 'Asia/Manila',
  'Asia/Riyadh', 'Asia/Seoul', 'Asia/Shanghai', 'Asia/Singapore',
  'Asia/Taipei', 'Asia/Tehran', 'Asia/Tokyo', 'Asia/Yangon',
  'Atlantic/Azores',
  'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Melbourne',
  'Australia/Perth', 'Australia/Sydney',
  'Europe/Amsterdam', 'Europe/Athens', 'Europe/Berlin', 'Europe/Brussels',
  'Europe/Dublin', 'Europe/Istanbul', 'Europe/Lisbon', 'Europe/London',
  'Europe/Madrid', 'Europe/Moscow', 'Europe/Paris', 'Europe/Rome',
  'Europe/Stockholm', 'Europe/Vienna', 'Europe/Warsaw', 'Europe/Zurich',
  'Pacific/Auckland', 'Pacific/Fiji', 'Pacific/Honolulu',
];

const formatOffset = (minutes: number): string => {
  const sign = minutes >= 0 ? '+' : '-';
  const abs = Math.abs(minutes);
  const h = String(Math.floor(abs / 60)).padStart(2, '0');
  const m = String(abs % 60).padStart(2, '0');
  return `UTC${sign}${h}:${m}`;
};

const getSupportedTimezones = (): string[] => {
  try {
    const intl = Intl as unknown as { supportedValuesOf?: (_key: string) => string[] };
    if (typeof intl.supportedValuesOf === 'function') {
      const zones = intl.supportedValuesOf('timeZone');
      if (Array.isArray(zones) && zones.length > 0) {
        return Array.from(new Set<string>(['UTC', ...zones]));
      }
    }
  } catch {
    /* fall through */
  }
  return FALLBACK_TIMEZONES;
};

export interface TimezoneOption {
  value: string;
  label: string;
  searchText: string;
  offsetMinutes: number;
}

let _cachedAllTimezones: TimezoneOption[] | null = null;

/**
 * Returns the full list of IANA timezones available on the current runtime,
 * each with a display label (e.g. "(UTC+05:30) Indian Standard Time (IST)")
 * and a precomputed lowercase `searchText` for fast filtering.
 *
 * UTC is pinned at the top. The rest are sorted by offset then by label so
 * geographically adjacent zones cluster together.
 *
 * Result is memoized — offsets are computed against the current moment, which
 * means a DST boundary crossing during a single session will display stale
 * offsets until the page is reloaded. Acceptable for a settings picker.
 */
export const getAllTimezones = (): TimezoneOption[] => {
  if (_cachedAllTimezones) return _cachedAllTimezones;

  const zones = getSupportedTimezones();
  const options: TimezoneOption[] = [];

  for (const zone of zones) {
    try {
      const dt = DateTime.now().setZone(zone);
      if (!dt.isValid) continue;
      const offsetMinutes = dt.offset;
      const offsetStr = formatOffset(offsetMinutes);
      const pretty = getTimezoneDisplayName(zone);
      const ianaDisplay = zone.replace(/_/g, ' ');
      const nameSegment = pretty === ianaDisplay ? zone : `${pretty} — ${zone}`;
      const label = `(${offsetStr}) ${nameSegment}`;
      options.push({
        value: zone,
        label,
        searchText: `${label} ${pretty} ${zone} ${ianaDisplay}`.toLowerCase(),
        offsetMinutes,
      });
    } catch {
      /* skip invalid zone */
    }
  }

  options.sort((a, b) => {
    if (a.value === 'UTC') return -1;
    if (b.value === 'UTC') return 1;
    if (a.offsetMinutes !== b.offsetMinutes) return a.offsetMinutes - b.offsetMinutes;
    return a.label.localeCompare(b.label);
  });

  _cachedAllTimezones = options;
  return options;
};

/**
 * Convert a local date range (Date objects) to UTC start/end timestamps, aligned to local days.
 */
export const convertDateRangeToUtcBounds = (
  startDate: Date,
  endDate: Date,
  timezone: string
): [string, string] => {
  const safeTimezone = timezone || 'UTC';
  const startOfDay = DateTime.fromJSDate(startDate, { zone: safeTimezone }).startOf('day');
  const endOfDay = DateTime.fromJSDate(endDate, { zone: safeTimezone }).endOf('day');

  const fallbackStart = DateTime.fromJSDate(startDate, { zone: 'UTC' }).startOf('day');
  const fallbackEnd = DateTime.fromJSDate(endDate, { zone: 'UTC' }).endOf('day');

  const utcStart =
    (startOfDay.isValid
      ? startOfDay.toUTC().toISO({ suppressMilliseconds: false })
      : fallbackStart.toUTC().toISO({ suppressMilliseconds: false })) || '';
  const utcEnd =
    (endOfDay.isValid
      ? endOfDay.toUTC().toISO({ suppressMilliseconds: false })
      : fallbackEnd.toUTC().toISO({ suppressMilliseconds: false })) || '';

  return [utcStart, utcEnd];
};


/**
 * Get difference in hours between two dates
 * @param from Start date
 * @param to End date
 * @returns Difference in hours
 */
export const getDifferenceInHours = (from: Date, to: Date) => {
  const diffMs = to.getTime() - from.getTime();
  return diffMs / (1000 * 60 * 60);
};

  // Helper function to convert local date to datetime-local format (YYYY-MM-DDTHH:MM)
  export const getLocalDateTimeString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };
